import { useState } from "react";

export type PinStage = "enter-new" | "confirm-new";

interface UseDoubleEntryPinOptions {
  minLength?: number;
  maxLength?: number;
}

/**
 * Pure UI-state hook for the "enter a new PIN, then type it again to
 * confirm" flow. Has no knowledge of HTTP, routing, or the user's current
 * PIN - it only tracks the two sequential digit-entry passes and whether
 * they match, so it can be reused by any keypad UI (forced change page,
 * future voluntary settings flow, etc).
 */
export function useDoubleEntryPin(options?: UseDoubleEntryPinOptions) {
  const minLength = options?.minLength ?? 4;
  const maxLength = options?.maxLength ?? 10;

  const [stage, setStage] = useState<PinStage>("enter-new");
  const [firstPin, setFirstPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [mismatchError, setMismatchError] = useState(false);

  const pinValue = stage === "enter-new" ? firstPin : confirmPin;

  const setDigit = (digit: string) => {
    setMismatchError(false);
    if (stage === "enter-new") {
      if (firstPin.length < maxLength) {
        setFirstPin(firstPin + digit);
      }
    } else {
      if (confirmPin.length < maxLength) {
        setConfirmPin(confirmPin + digit);
      }
    }
  };

  const backspace = () => {
    setMismatchError(false);
    if (stage === "enter-new") {
      setFirstPin(firstPin.slice(0, -1));
    } else {
      setConfirmPin(confirmPin.slice(0, -1));
    }
  };

  const advance = (): boolean => {
    if (stage === "enter-new") {
      if (firstPin.length < minLength) {
        return false;
      }
      setStage("confirm-new");
      return true;
    }

    if (confirmPin.length < minLength) {
      return false;
    }
    if (confirmPin !== firstPin) {
      setMismatchError(true);
      setConfirmPin("");
      return false;
    }
    return true;
  };

  const reset = () => {
    setStage("enter-new");
    setFirstPin("");
    setConfirmPin("");
    setMismatchError(false);
  };

  const isComplete = stage === "confirm-new" && confirmPin.length >= minLength && confirmPin === firstPin;

  return {
    stage,
    pinValue,
    firstPin,
    confirmPin,
    setDigit,
    backspace,
    advance,
    reset,
    mismatchError,
    isComplete,
    minLength,
    maxLength,
  };
}
